<template>
  <div class="modern-form manage-admin-wrapper">
    <v-container>
      <v-row class="mt-3">
        <v-col cols="12" sm="12" md="4">
          <div class="user-sidebar">
            <div class="sidebar-header">
              <h3 class="sidebar-title">Select User</h3>
              <div class="search-row">
                <v-text-field
                  label="Search"
                  v-model="search"
                  outlined
                  dense
                  hide-details
                  @keyup.enter="getUsers()"
                  class="search-input" />
                <v-btn
                  class="btn-primary search-btn"
                  @click="getUsers()">Search</v-btn>
              </div>
            </div>
            <div class="sidebar-content">
              <div class="user-list">
                <div class="text-center">
                  <v-progress-circular
                    v-if="loading"
                    indeterminate
                    color="primary"
                    class="loading-spinner" />
                </div>
                <div v-if="!loading && users.length === 0" class="no-results">
                  <v-icon class="no-results-icon">mdi-account-off</v-icon>
                  <p class="no-results-text">No users found</p>
                </div>
                <div v-if="!loading && users.length > 0" class="users-list" id="usersList">
                  <div
                    v-for="(item, i) in users"
                    :key="i"
                    @click="selectUser(item); selectedUserIndex = i"
                    class="user-item"
                    :class="{ 'selected': selectedUserIndex === i }">
                    <v-avatar class="user-avatar" size="36">
                      <span class="white--text">{{item.firstName.charAt(0) + item.lastName.charAt(0)}}</span>
                    </v-avatar>
                    <span class="user-name">{{item.firstName + ' ' + item.lastName + (isAdminUser ? ` (${item.roleId === 2 ? 'Client User' : 'Admin'})` : '') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="pagination-container">
            <div class="pagination-size">
              <v-select
                v-model="pageSize"
                :items="pageSizes"
                label="Items"
                dense
                outlined
                hide-details
                @change="handlePageSizeChange"></v-select>
            </div>
            <v-pagination
              v-model="page"
              :length="totalPages"
              total-visible="5"
              next-icon="mdi-menu-right"
              prev-icon="mdi-menu-left"
              class="custom-pagination">
            </v-pagination>
          </div>
        </v-col>
        <v-col cols="12" sm="12" md="8">
          <div v-if="loading" class="loading-container">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              class="loading-spinner" />
          </div>
          <div v-else-if="!form.userId && Object.keys(form).length === 0 && users.length === 0" class="no-selection">
            <v-icon class="no-selection-icon">mdi-account-search</v-icon>
            <p class="no-selection-text">No users were found</p>
            <v-btn class="btn-primary mt-4" @click="addUser()">
              <v-icon left small>mdi-plus</v-icon>
              Add User
            </v-btn>
          </div>
          <div v-else class="info-section">
            <div class="section-header-with-actions">
              <h2 class="section-title">User Settings</h2>
              <v-btn class="btn-primary" @click="addUser()">
                <v-icon left small>mdi-plus</v-icon>
                Add User
              </v-btn>
            </div>
            <div class="form-row">
              <v-text-field
                ref="firstNameInput"
                v-model="form.firstName"
                label="First Name"
                class="line-input custom-filled"
                dense
                hide-details="auto" />
              <v-text-field v-model="form.lastName" label="Last Name" class="line-input custom-filled" dense hide-details="auto" />
            </div>
            <div class="form-row">
              <v-text-field v-model="form.email" label="Email" :rules="[rules.email, rules.required]" class="line-input custom-filled" dense hide-details="auto" />
              <v-text-field v-model="form.phone" label="Phone" :rules="[rules.phone]" class="line-input custom-filled" dense hide-details="auto" />
            </div>
            <div class="form-row">
              <v-text-field v-model="form.providerId" label="OD Provider Id" :rules="[rules.numeric]" class="line-input custom-filled" dense hide-details="auto" />
              <v-text-field v-model="form.employeeId" label="HR Employee Id" :rules="[rules.numeric]" class="line-input custom-filled" dense hide-details="auto" />
            </div>
            <div class="form-row" v-if="isAdminUser">
              <v-select v-model="form.roleId" label="Role" class="line-input custom-filled" dense hide-details="auto" :items="roles" item-text="label" />
              <v-select v-model="form.reportingRoleId" label="Clinic Role" class="line-input custom-filled" dense hide-details="auto" :items="reportingRoles" item-text="label" />
            </div>
            <div class="form-actions">
              <v-btn class="btn-primary" @click="saveUser()">Save</v-btn>
              <v-btn class="btn-secondary" @click="cancel()">Cancel</v-btn>
              <v-btn class="btn-warning" :disabled="!form.userId" @click="showConfirmDeleteDialog = true">Delete</v-btn>
            </div>
          </div>

          <div class="info-section" v-if="!loading && form.userId">
            <h2 class="section-title">Actions</h2>
            <div class="action-buttons-container">
              <v-btn @click="showPasswordResetDialog = true" class="btn-secondary">
                <v-icon left>mdi-lock-reset</v-icon>
                Send Password Reset Link
              </v-btn>
              <v-btn @click="showConfirmPasswordDialog = true" class="btn-secondary">
                <v-icon left>mdi-lock-open-variant</v-icon>
                Generate New Password
              </v-btn>
            </div>
          </div>
        </v-col>
        <v-dialog v-model="showPasswordResetDialog" max-width="500px" persistent>
          <v-card class="modern-modal">
            <v-card-title class="modal-title">Send Password Reset</v-card-title>
            <v-card-text class="modal-content">
              <p class="modal-question">Send password reset link to {{ form.email }}?</p>
              <p class="modal-description">The user will receive an email with instructions to reset their password.</p>
            </v-card-text>
            <v-card-actions class="modal-actions">
              <v-btn class="btn-secondary" @click="showPasswordResetDialog = false">Cancel</v-btn>
              <v-btn class="btn-primary" @click="sendPasswordReset()">Send Reset Link</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="showConfirmDeleteDialog" max-width="500px" persistent>
          <v-card class="modern-modal">
            <v-card-title class="modal-title">Delete User</v-card-title>
            <v-card-text class="modal-content">
              <p class="modal-question">Are you sure you want to delete {{ form.firstName }} {{ form.lastName }} ({{ form.email }})?</p>
              <p class="modal-description">This action cannot be undone.</p>
            </v-card-text>
            <v-card-actions class="modal-actions">
              <v-btn class="btn-secondary" @click="showConfirmDeleteDialog = false">Cancel</v-btn>
              <v-btn class="btn-warning" @click="deleteUser()">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="showConfirmPasswordDialog" max-width="500px" persistent>
          <v-card class="modern-modal">
            <v-card-title class="modal-title">Generate New Password</v-card-title>
            <v-card-text class="modal-content">
              <p class="modal-question">Generate a new password for {{ form.email }}?</p>
              <p class="modal-description">A new password will be created and emailed to the user.</p>
            </v-card-text>
            <v-card-actions class="modal-actions">
              <v-btn class="btn-secondary" @click="showConfirmPasswordDialog = false">Cancel</v-btn>
              <v-btn class="btn-primary" @click="changeAndSendPassword()">Generate & Send</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </v-container>
  </div>
</template>
<script>
import DataService from '../../../_services/DataService';
import ValidateService from '../../../_services/ValidateService';
export default {
  name: 'AdminManagement',
  props: ['template_props'],
  components: {},
  data() {
    return {
      page: 1,
      totalPages: 0,
      pageSizes: [10, 25, 50, 100, 250],
      pageSize: 10,
      pageName: 'User Settings',
      loading: true,
      search: '',
      isAdminUser: false,
      isVerifiedUser: false,
      showConfirmDeleteDialog: false,
      showConfirmPasswordDialog: false,
      showPasswordResetDialog: false,
      selectedUser: 0,
      selectedUserIndex: null,
      previousSelectedUserIndex: null,
      tabs: [
        { label: 'Admin Management', section: '' },
        { label: 'Patient Management', section: '' },
        { label: 'Schedule Management', section: '' },
      ],
      roles: [
        { value: 1, label: 'Admin' },
        { value: 2, label: 'Client User' },
      ],
      reportingRoles: [],
      form: {},
      originalForm: {},
      rules: ValidateService.validators,
    };
  },
  watch: {
    page: {
      async handler() {
        this.getUsers();
      },
    },
  },
  methods: {
    addUser() {
      this.previousSelectedUserIndex = this.selectedUserIndex;
      this.form = { userId: null };
      this.selectedUserIndex = null;
      this.$nextTick(() => {
        if (this.$refs.firstNameInput) {
          this.$refs.firstNameInput.focus();
        }
      });
    },
    async handlePageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.getUsers();
    },
    async changeAndSendPassword(alert = true) {
      this.showConfirmPasswordDialog = false;
      try {
        const response = await DataService.changeAndSendPassword({ username: this.form.email });
        if (response.status == 200) {
          if (alert) {
            this.$root.$emit('raise_alert', {
              color: 'success',
              text: 'New password sent to ' + this.form.email,
            });
          }
          return true;
        }
      } catch (err) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: err.response?.data || 'Something went wrong',
        });
      }
      return false;
    },
    async sendPasswordReset() {
      this.showPasswordResetDialog = false;
      try {
        const response = await DataService.sendPasswordResetEmail({ username: this.form.email });
        if (response.status == 200) {
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'Reset link sent to ' + this.form.email,
          });
        }
      } catch (err) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: err.response.data || 'Something went wrong',
        });
      }
    },
    async saveUser() {
      if (!this.form.userId) {
        const creationResponse = await DataService.createClientUserAccount(this.form);
        if (creationResponse.status === 201) {
          await this.changeAndSendPassword(false);
          
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'User created successfully!',
          });
          
          // Construct the user object to match getDisplayData format
          const newUser = {
            firstName: creationResponse.data.user.firstName,
            lastName: creationResponse.data.user.lastName,
            email: creationResponse.data.user.email,
            phone: creationResponse.data.user.phone,
            userId: creationResponse.data.user.id,
            roleId: creationResponse.data.roleId,
            reportingRoleId: creationResponse.data.reportingRoleId,
          };
          
          this.selectUser(newUser);
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: creationResponse.data || 'Something went wrong',
          });
        }
      } else {
        const updateResponse = await DataService.updateUser(this.form.userId, this.form);
        if (updateResponse.status == 200) {
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'User updated successfully!',
          });
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: updateResponse.data || 'Something went wrong',
          });
        }
      }
    },
    cancel() {
      if (!this.form.userId) {
        // Was adding a new user - restore previous selection
        this.form = Object.assign({}, this.originalForm);
        this.selectedUserIndex = this.previousSelectedUserIndex;
      } else {
        // Was editing - just undo changes, keep current selection
        this.form = Object.assign({}, this.originalForm);
      }
    },
    selectUser(item) {
      this.form = Object.assign({}, item);
      this.originalForm = Object.assign({}, this.form);
    },
    async getUsers() {
      this.loading = true;
      this.users = [];
      this.selectedUserIndex = null;
      let params = `?page=${this.page - 1}&size=${this.pageSize}`;
      if (this.search) {
        params = `?page=${this.page - 1}&size=${this.pageSize}&searchTerm=${this.search}`;
      }
      const response = await DataService.getClientUserAccounts(params);
      if (response.status === 200) {
        this.totalPages = Math.ceil(response.data.count / this.pageSize) || 0;

        if (response.data.error) {
          this.$root.$emit('raise_alert', {
            color: 'info',
            text: response.data.error,
          });
        }
        const users = response.data.rows;
        this.users = users.map(this.getDisplayData);
        this.loading = false;

        if (this.users.length > 0) {
          this.selectUser(this.users[0]);
          this.selectedUserIndex = 0;
        }
      }
    },
    async getReportingRoles() {
      this.reportingRoles = [];
      const response = await DataService.getReportingRoles();
      if (response.status === 200) {
        this.reportingRoles = response.data.map((role) => {
          return {
            value: role.id,
            label: role.name === 'PCC' ? 'PCC (Patient Care Coordinator)' : role.name,
          };
        });
      }
    },
    getDisplayData(d) {
      return {
        firstName: d.user.firstName,
        lastName: d.user.lastName,
        email: d.user.email,
        phone: d.user.phone,
        userId: d.user.id,
        roleId: d.roleId,
        employeeId: d.employeeId,
        providerId: d.providerId,
        reportingRoleId: d.reportingRoleId,
      };
    },
    async deleteUser() {
      this.showConfirmDeleteDialog = false;
      const deleted = await DataService.deleteClientUserAccount(this.form.userId);
      if (deleted.status === 200) {
        this.$root.$emit('raise_alert', {
          color: 'success',
          text: 'User deleted successfully',
        });
        await this.getUsers();
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: deleted.data || 'Something went wrong',
        });
      }
    },
  },
  async mounted() {
    this.isAdminUser = localStorage.getItem('role') === 'clientAdmin';

    await Promise.all([
      this.getUsers(),
      this.getReportingRoles(),
    ]);

    if (this.users.length > 0) {
      this.selectUser(this.users[0]);
      this.selectedUserIndex = 0;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';

$user-card-border-color: #e2e8f0;
$section-title-color: #1f2937;
$action-button-gap: 12px;
$modal-description-color: #6b7280;
$search-input-color: #414a5b;

.user-sidebar {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: fit-content;
  
  .sidebar-header {
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    
    .sidebar-title {
      font-size: 18px;
      font-weight: 600;
      color: #2d3748;
      margin: 0 0 16px 0;
      font-family: 'Inter', sans-serif;
    }
    
    .search-row {
      display: flex;
      gap: 8px;
      align-items: flex-start;
      
      .search-input {
        flex: 1;
        min-width: 0;
        
        ::v-deep input {
          color: $search-input-color;
        }
        
        ::v-deep fieldset {
          border-width: 2px;
        }
      }
      
      .search-btn {
        flex-shrink: 0;
        min-width: 80px;
      }
    }
  }
  
  .sidebar-content {
    padding: 16px;
  }
}

.user-list {
  .no-results {
    text-align: center;
    padding: 40px 16px;
    
    .no-results-icon {
      font-size: 48px;
      color: #cbd5e0;
      margin-bottom: 12px;
    }
    
    .no-results-text {
      color: #718096;
      margin: 0;
      font-size: 14px;
    }
  }
  
  .users-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .user-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: #edf2f7;
      border-color: #cbd5e0;
    }
    
    &.selected {
      background-color: #f0f9e8 !important;
      border-color: #B0D35E !important;
      
      .user-name {
        color: #414a5b !important;
        font-weight: 600 !important;
      }
    }
    
    .user-avatar {
      background-color: #133b54 !important;
      color: white !important;
      margin-right: 12px !important;
    }
    
    .user-name {
      font-weight: 500 !important;
      color: #1f2937 !important;
      font-size: 14px !important;
    }
  }
}

.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 12px 0;
  padding-left: 16px;
  
  .pagination-size {
    width: 90px;
  }
  
  .custom-pagination {
    ::v-deep .v-pagination {
      &__item {
        box-shadow: none;
        border: 1px solid #e2e8f0;
        
        &--active {
          background-color: #133b54 !important;
          color: white !important;
          border-color: #133b54 !important;
        }
      }

      &__more {
        width: 22px;
        margin-left: 0;
        margin-right: 0;
      }
      
      &__navigation {
        box-shadow: none;
        border: 1px solid #e2e8f0;
      }
    }
  }
}

.no-selection {
  text-align: center;
  padding: 80px 24px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  
  .no-selection-icon {
    font-size: 64px;
    color: #cbd5e0;
    margin-bottom: 16px;
  }
  
  .no-selection-text {
    color: #718096;
    margin: 0;
    font-size: 16px;
  }
}

.loading-container {
  text-align: center;
  padding: 80px 24px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
}

.section-header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: $section-title-color;
    margin: 0;
    border: none;
  }
}

.action-buttons-container {
  display: flex;
  flex-wrap: wrap;
  gap: $action-button-gap;
  margin-top: 16px;
}

.v-card {
  border: 1px solid $user-card-border-color !important;
  box-shadow: $shadow-sm !important;
}

.loading-spinner {
  display: block;
  margin: 40px auto;
}

.modal-description {
  color: $modal-description-color;
  font-size: 14px;
  margin-top: 12px;
}

// Wrapper styling to prevent pagination overflow
// When there are many pages, the pagination can extend beyond the container width
.manage-admin-wrapper {
  .v-container {
    padding-bottom: 32px;
    max-width: 100%;
    overflow-x: hidden;
  }

  .pagination-container {
    max-width: 100%;
    overflow-x: auto;
  }
}
</style>
